import React, { useState, useEffect } from "react";
import { useAuth } from "components/ui/navigationGuard";
import axiosInstance from "utils/axiosInstance";
import LeaveForm from "./components/leaveForm";
import LeaveList from "./components/leaveList";
import Modal from "components/ui/modal";
import Button from "components/ui/button";
import Icon from "components/appIcon";
import AdminNavigation from "layouts/adminNavigation";
import EmployeeNavigation from "layouts/employeeNavigation";
import AllLeaveRequests from "./components/allLeaveRequests";

const LeaveManagement = () => {
    const { user } = useAuth(); // user = { id, role, employee_type }
    console.log("User data:", user);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [loading, setLoading] = useState(true);
    const [leaves, setLeaves] = useState([]);
    const [leaveTypes, setLeaveTypes] = useState([]); // ✅ dynamic leave types
    const [isModalOpen, setIsModalOpen] = useState(false);

    const [form, setForm] = useState({
        employee_id: user?.id,           
        employee_type: user?.employee_type,
        leave_type: "",                 
        start_date: "",
        end_date: "",
        reason: "",
    });

    // Fetch leave requests
    const fetchLeaves = async () => {
        try {
            let res;
            if (user.role === "admin") {
                res = await axiosInstance.get("/leave/leave/all");
                setLeaves(res?.data || []);
            } else {
                res = await axiosInstance.get(`/leave/leave/employee/${user?.id}`);
                setLeaves(res?.data || []);
            }
        } catch (err) {
            console.error("Failed to fetch leaves:", err);
        } finally {
            setLoading(false);
        }
    };

    // Fetch allowed leave types for employee
    const fetchLeaveTypes = async () => {
        if (user.role !== "employee") return;
        try {
            const res = await axiosInstance.get(
                `/leave/leave/types?employee_type=${user.employee_type}`
            );
            setLeaveTypes(res.data || []);

            // default form type = first available feature
            if (res.data?.length > 0) {
                setForm((prev) => ({ ...prev, leave_type: res.data[0].feature }));
            }
        } catch (err) {
            console.error("Failed to fetch leave types:", err);
        }
    };


    useEffect(() => {
        if (!user?.id) return;
        fetchLeaves();
        fetchLeaveTypes();
    }, [user]);

    // Handle form change
    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    // Employee: Apply leave
    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await axiosInstance.post("/leave/leave", form);
            // clear form + close modal
            setForm({ employee_id: user?.id,employee_type: user?.employee_type,leave_type: leaveTypes[0] || "", start_date: "", end_date: "", reason: "" });
            setIsModalOpen(false);
            // ✅ refresh from backend
            fetchLeaves();
        } catch (err) {
            console.error("Failed to apply leave:", err);
        }
    };

    // Admin: Update leave status
    const handleStatusChange = async (leaveId, status) => {
        try {
            const res = await axiosInstance.put(
                `/leave/leave/${leaveId}/status?status=${status}`
            );
            setLeaves((prev) =>
                prev.map((leave) => (leave.id === leaveId ? res.data : leave))
            );
        } catch (err) {
            console.error("Failed to update status:", err);
        }
    };

    const handleToggleCollapse = () => setIsCollapsed(!isCollapsed);

    if (loading) {
        return (
            <div className="min-h-screen bg-background">
                {user?.role === "admin" ? <AdminNavigation /> : <EmployeeNavigation />}
                <div className="pt-16 flex items-center justify-center min-h-[calc(100vh-4rem)]">
                    <div className="flex flex-col items-center space-y-4">
                        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                        <p className="text-sm text-muted-foreground">Loading leave data...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background">
            {/* ✅ Show correct navigation */}
            {user?.role === "admin" ? (
                <AdminNavigation
                    user={user}
                    isCollapsed={isCollapsed}
                    onToggleCollapse={handleToggleCollapse}
                />
            ) : (
                <EmployeeNavigation
                    user={user}
                    isCollapsed={isCollapsed}
                    onToggleCollapse={handleToggleCollapse}
                />
            )}

            <main
                className={`pt-16 transition-all duration-200 ease-in-out ${isCollapsed ? "lg:pl-16" : "lg:pl-64"
                    }`}
            >
                <div className="p-4 sm:p-6 lg:p-8 space-y-8">
                    {/* Employee UI */}
                    {user.role === "employee" && (
                        <>
                            <div className="flex justify-end">
                                <button
                                    onClick={() => setIsModalOpen(true)}
                                    className="bg-primary text-white px-4 py-2 rounded shadow"
                                >
                                    + Add Leave
                                </button>
                            </div>
                            <LeaveList data={leaves} />
                        </>
                    )}

                    {user.role === "admin" && (
                        <>
                            <h2 className="text-xl font-bold mb-4">All Leave Requests</h2>
                            <AllLeaveRequests
                                leaves={leaves}
                                handleStatusChange={handleStatusChange}
                            />
                        </>
                    )}
                </div>
            </main>

            {/* Employee Leave Modal */}
            {user.role === "employee" && (
                <Modal isOpen={isModalOpen} onClose={setIsModalOpen}>
                    <div className="flex items-center justify-between p-6 border-b border-border">
                        <div>
                            <h2 className="text-xl font-semibold text-foreground">
                                Apply for Leave
                            </h2>
                            <p className="text-sm text-muted-foreground mt-1">
                                Fill out the form below to apply for leave
                            </p>
                        </div>
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setIsModalOpen(false)}
                        >
                            <Icon name="X" size={20} />
                        </Button>
                    </div>
                    <div className="p-6 overflow-y-auto max-h-[90vh]">
                        <LeaveForm
                            form={form}
                            onChange={handleChange}
                            onSubmit={handleSubmit}
                            onClose={() => setIsModalOpen(false)}
                            leaveTypes={leaveTypes} 
                            setForm={setForm}
                        />
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default LeaveManagement;
